import React from 'react'
import {
  FilledInput,
  FormHelperText,
  FormLabel,
  MenuItem,
  FormControl,
  FormControlLabel,
  ListItemText,
  Checkbox,
  Select,
  Options
} from '@fedsim/components'
import { venues } from '@fedsim/constants';
import { numberFormatWholeNumber } from "@fedsim/helpers";
import { Controller } from 'react-hook-form'
import {
  getModeledWrestlers,
  getShows,
  getSegments,
  getChampionships,
  getBrands
} from 'selectors'
import { useSelector } from 'react-redux'

import ORMSegment from 'contexts/Segment/model'
import { showFrequency } from 'contexts/Show/model'

const segmentByType = type => ORMSegment.types?.[type]

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

export const FormSingleSelect = ({
  options = [],
  name = '',
  control,
  label = '',
  fullWidth = true
}) => {
  // return null
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = '' }, fieldState: { error } }) => {
        const input = <FilledInput label={label} />
        const changeHandler = ev => onChange(ev.target.value)
        const renderValue = () => {
          const filtered = options.filter(item =>
            typeof value === 'number'
              ? value === item.id
              : value?.includes(item.id)
          )
          const mapped = filtered.map(item => item.name)
          return mapped.join(',')
        }
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Select
              labelId='multiple-checkbox-label'
              id={`multiple-checkbox-id-${label}`}
              multiple={false}
              value={value}
              error={error}
              fullWidth={fullWidth}
              onChange={changeHandler}
              input={input}
              renderValue={renderValue}
              MenuProps={MenuProps}
            >
              {options.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  <Checkbox
                    checked={
                      typeof value === 'number'
                        ? value === id
                        : value?.includes(id)
                    }
                  />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }}
    />
  )
}

export const FormMultipleSelect = ({
  multiple = true,
  options = [],
  name = '',
  control,
  label = '',
  fullWidth = true
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        const input = <FilledInput label={label} />
        const changeHandler = ev => onChange(ev.target.value)
        const renderValue = () => {
          const filtered = options.filter(item =>
            typeof value === "number"
              ? value === item.id
              : value?.includes(item.id)
          )
          const mapped = filtered.map(item => item.name)
          return mapped.join(', ')
        }
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Select
              labelId="multiple-checkbox-label"
              id={`multiple-checkbox-id-${label}`}
              multiple={multiple}
              value={value}
              error={error}
              fullWidth={fullWidth}
              onChange={changeHandler}
              input={input}
              renderValue={renderValue}
              MenuProps={MenuProps}
            >
              {options.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  <Checkbox
                    checked={
                      typeof value === 'number'
                        ? value === id
                        : value?.includes(id)
                    }
                  />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }}
    />
  )
}

export const FormMultipleSelectChampionships = ({
  name = 'championshipIds',
  label = '',
  ...props
}) => {
  const options = useSelector(getChampionships)

  return (
    <FormMultipleSelect
      name={name}
      label={label}
      options={options}
      {...props}
    />
  )
}

function ucFirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const FormFrequencySelect = ({ name = 'brandId', label = '', ...props }) => {
  const options = Object.keys(showFrequency).map(frequency => ({
    id: frequency,
    name: ucFirst(frequency),
    value: frequency
  }))

  return (
    <FormSingleSelect name={name} label={label} options={options} {...props} />
  )
}

export const FormSelectBrand = ({ name = 'brandId', label = '', ...props }) => {
  const options = useSelector(getBrands)

  return (
    <FormSingleSelect name={name} label={label} options={options} {...props} />
  )
}

export const FormSelectWrestler = ({
  name = 'wrestlerId',
  label = '',
  ...props
}) => {
  const options = useSelector(getModeledWrestlers)

  return (
    <FormSingleSelect name={name} label={label} options={options} {...props} />
  )
}

export const FormSelectDirector = ({
  name = 'directorId',
  label = '',
  ...props
}) => {
  return FormSelectWrestler({ name, label, ...props })
}

export const FormSelectChampionship = ({
  name = 'championshipId',
  label = '',
  ...props
}) => {
  const options = useSelector(getChampionships)

  return (
    <FormSingleSelect name={name} label={label} options={options} {...props} />
  )
}

export const FormMultipleSelectSegments = ({
  name = 'segmentIds',
  label = '',
  ...props
}) => {
  const options = useSelector(getSegments)

  return (
    <FormMultipleSelect
      name={name}
      label={label}
      options={options}
      {...props}
    />
  )
}
export const FormMultipleSelectBrands = ({
  name = 'brandIds',
  label = '',
  ...props
}) => {
  const options = useSelector(getBrands)

  return (
    <FormMultipleSelect
      name={name}
      label={label}
      options={options}
      {...props}
    />
  )
}

export const FormSelectShow = ({
  name = 'showId',
  label = '',
  value,
  ...props
}) => {
  const options = useSelector(getShows)

  return (
    <FormSingleSelect
      name={name}
      value={value}
      label={label}
      options={options}
      {...props}
    />
  )
}

export const FormMultipleSelectShows = ({
  name = 'showIds',
  label = '',
  ...props
}) => {
  const options = useSelector(getShows)

  return (
    <FormMultipleSelect
      name={name}
      label={label}
      options={options}
      {...props}
    />
  )
}

export const FormWeekSelect = ({
  name = 'week',
  label = '',
  control,
  ...props
}) => {
  const rawOptions = ['First week', 'Second week', 'Third week', 'Last week']

  const options = rawOptions.map((option, index) => ({
    id: index,
    value: index,
    active: option,
    inactive: `${option}?`
  }))

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        const forwardResponse = (event) => {
          event.target.value = Number(event.target.value)
          onChange(event)
        }
        const clickHandle = (event) => event.target.value === "on" ? onChange(false) : forwardResponse(event)

        return (
          <>
            {error && <FormHelperText>{error.message}</FormHelperText>}
            <FormControl fullWidth>
              <FormLabel>{label}</FormLabel>
              <FormControlLabel control={<Checkbox checked={value === false} onClick={clickHandle} />} label="None" />
              <Options value={value} onChange={onChange} options={options} />
            </FormControl>
          </>
        )
      }}
    />
  )
}

export const FormMonthSelect = ({
  name = 'month',
  label = '',
  control,
  ...props
}) => {
  const rawOptions = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const options = rawOptions.map((option, index) => ({
    id: index,
    value: index,
    active: option,
    inactive: `${option}?`
  }))

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        const clickHandle = (event) => event.target.value === "on" ? onChange(false) : onChange(event)

        return (
          <>
            {error && <FormHelperText>{error.message}</FormHelperText>}
            <FormControl fullWidth>
              <FormLabel>{label}</FormLabel>
              <FormControlLabel control={<Checkbox checked={value === false} onClick={clickHandle} />} label="None" />
              <Options value={value} onChange={onChange} options={options} />
            </FormControl>
          </>
        )
      }}
    />
  )
}

export const FormSelectDayOfWeek = ({
  name = 'dayIndex',
  label = '',
  control
}) => {
  const rawOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  const options = rawOptions.map((option, index) => ({
    id: index,
    value: index,
    active: option,
    inactive: `${option}?`
  }))

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Options value={value} onChange={onChange} options={options} />
          </FormControl>
        )
      }}
    />
  )
}

const GENDERS = [
  {
    id: 10,
    value: 'MALE',
    inactive: 'Male?',
    active: 'Male'
  },
  {
    id: 11,
    value: 'FEMALE',
    inactive: 'Female?',
    active: 'Female'
  }
]

export const FormSelectGender = ({ name = 'gender', label = '', control }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Options value={value} onChange={onChange} options={GENDERS} />
          </FormControl>
        )
      }}
    />
  )
}

export const FormSelectSegmentTypes = ({
  name = 'segmentIds',
  label = '',
  control
}) => {
  const options = Object.keys(ORMSegment.types).map(type => {
    const byType = segmentByType(type)
    return {
      id: type,
      value: type,
      active: `${byType?.name} (cost: ${byType.cost})`,
      inactive: `${byType?.name}? (cost: ${byType.cost})`
    }
  })

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Options value={value} onChange={onChange} options={options} />
          </FormControl>
        )
      }}
    />
  )
}

const ALIGNMENTS = [
  {
    id: 10,
    value: 'BAD',
    inactive: 'Bad?',
    active: 'Bad'
  },
  {
    id: 11,
    value: 'GOOD',
    inactive: 'Good?',
    active: 'Good'
  },
  {
    id: 11,
    value: 'NEUTRAL',
    inactive: 'Neutral?',
    active: 'Neutral'
  },
]

export const FormSelectAlignment = ({ name = 'alignment', label = 'Alignment', control }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        return (
          <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            <Options value={value} onChange={onChange} options={ALIGNMENTS} />
          </FormControl>
        )
      }}
    />
  )
}

// export const FormSelectShow = ({
//   name = 'showId',
//   label = '',
//   value,
//   ...props
// }) => {
//   const options = useSelector(getShows)

//   return (
//     <FormSingleSelect
//       name={name}
//       value={value}
//       label={label}
//       options={options}
//       {...props}
//     />
//   )
// }

export const FormSelectVenue = ({ name = 'venueId', label = 'Venue', value = 'DEFAULT', ...props }) => {
  const options = venues.map(venue => ({
    id: venue.id,
    name: `${venue.name}, ${venue.city}, ${venue.country} (${numberFormatWholeNumber(venue.capacity)})`,
  }));

  return (
    <FormSingleSelect
      name={name}
      value={value}
      label={label}
      options={options}
      {...props}
    />
  )
}


export default FormMultipleSelect
